// components/SelectedMenuItem/SelectedMenuItem.jsx
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";

// CSS
import "./selected-menu-item.css";

// Context
import { useCart } from "../../context/CartContext";

// Icons
import { FaMinus, FaPlus } from "react-icons/fa";

const SelectedMenuItem = ({ menuItem, closeModal }) => {
  const { cart, setCart } = useCart();
  const [quantity, setQuantity] = useState(1);

  // The same dish can be priced differently per meal-time category (lunch
  // vs dinner vs late-night), so which category this was opened under
  // matters for both price and cart identity - falls back to the
  // cheapest listed category if the caller didn't specify one (mango/
  // platter items don't have categoryPricing at all and skip this).
  const selectedCategory =
    menuItem?.selectedCategory ||
    (menuItem?.categoryPricing
      ? [...menuItem.categoryPricing].sort((a, b) => a.price - b.price)[0].category
      : undefined);
  const price = menuItem?.categoryPricing
    ? menuItem.categoryPricing.find((cp) => cp.category === selectedCategory)?.price
    : menuItem?.price;

  useEffect(() => {
    const cartItem = cart.find(
      (item) => item._id === menuItem?._id && item.category === selectedCategory
    );
    if (cartItem) {
      setQuantity(cartItem.quantity);
    } else {
      setQuantity(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuItem, selectedCategory]);

  // 🔥 FIRE VIEWCONTENT EVENT WHEN MODAL OPENS
  useEffect(() => {
    if (menuItem && window.fbq) {
      window.fbq("track", "ViewContent", {
        content_ids: [menuItem._id],
        content_name: menuItem.title,
        content_category: selectedCategory || "General",
        content_type: "product",
        value: price,
        currency: "BDT",
        // Optional: Add description for better insights
        content_description: menuItem.desc?.substring(0, 100),
      });

      console.log("👁️ ViewContent event fired for:", menuItem.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuItem]);

  const increaseQuantity = () => setQuantity(quantity + 1);
  const decreaseQuantity = () => setQuantity(quantity > 1 ? quantity - 1 : 1);

  const handleAddToCart = () => {
    // A cart can only hold one restaurant's items at a time (matches the
    // server's own check in chatRoute.js). Mango/platter items have no
    // restaurant at all and are exempt - they're site-wide and can sit
    // alongside any restaurant's items.
    if (menuItem.restaurant) {
      const conflictingItem = cart.find(
        (item) => item.restaurant && item.restaurant !== menuItem.restaurant._id
      );
      if (conflictingItem) {
        const confirmed = window.confirm(
          `Your cart has items from another restaurant. Adding this will clear your cart and start a new order from ${menuItem.restaurant.name}. Continue?`
        );
        if (!confirmed) return;
      }
    }

    // Items from a different (now-abandoned) restaurant are dropped here
    // rather than via a separate setCart([]) call above - two setCart
    // calls in the same handler would just have the second silently win
    // anyway, since React batches them.
    const baseCart = menuItem.restaurant
      ? cart.filter((item) => !item.restaurant || item.restaurant === menuItem.restaurant._id)
      : cart;
    const updatedCart = [...baseCart];
    const existingItemIndex = updatedCart.findIndex(
      (item) => item._id === menuItem._id && item.category === selectedCategory,
    );

    // Cart entries snapshot their own price/category/restaurant at
    // add-time - every downstream consumer (Cart totals, checkout
    // submission, analytics) reads these off the cart entry itself, not
    // the catalog item, so this is the only place that needs to resolve
    // categoryPricing or the restaurant object down to just its id.
    const cartEntry = {
      ...menuItem,
      category: selectedCategory,
      price,
      quantity,
      restaurant: menuItem.restaurant?._id,
    };
    delete cartEntry.categoryPricing;
    delete cartEntry.selectedCategory;

    if (existingItemIndex !== -1) {
      updatedCart[existingItemIndex] = cartEntry;
    } else {
      updatedCart.push(cartEntry);
    }
    toast("Item added to cart!");
    setCart(updatedCart);

    // 🔥 Optional: Track AddToCart event too (good for funnel analysis)
    if (window.fbq) {
      window.fbq("track", "AddToCart", {
        content_ids: [menuItem._id],
        content_name: menuItem.title,
        content_type: "product",
        value: price * quantity,
        currency: "BDT",
      });
      console.log("🛒 AddToCart event fired for:", menuItem.title);
    }
  };

  return (
    <div className="selected-menu-item">
      <div className="selected-menu-item-container">
        <img
          src={menuItem.imageUrl[0]}
          alt={menuItem.title}
          className="selected-menu-item-img"
        />

        <div className="selected-menu-item-details">
          <h2 className="selected-menu-item-title">{menuItem.title}</h2>
          {menuItem.categoryPricing && (
            <p className="text-sm text-accent font-semibold uppercase tracking-wide">
              {selectedCategory.replace(/_/g, " ")}
            </p>
          )}
          <p className="selected-menu-item-price">Price: ৳{price}</p>

          <div className="selected-menu-item-quantity-container">
            <FaMinus className="quantity-minus" onClick={decreaseQuantity} />
            <input
              type="number"
              className="selected-menu-item-quantity"
              value={quantity}
              min="1"
              onChange={(e) => {
                let value = parseInt(e.target.value, 10);
                if (isNaN(value) || value < 1) value = 1;
                setQuantity(value);
              }}
            />
            <FaPlus className="quantity-plus" onClick={increaseQuantity} />
          </div>

          <p className="selected-menu-item-total">
            <span>Total:</span>
            <span className="total">৳{price * quantity}</span>
          </p>
          <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick={false}
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
          />

          <div className="selected-menu-item-btns">
            <button
              onClick={handleAddToCart}
              className="add-to-cart-btn btn btn-primary"
            >
              Add to Cart
            </button>
            <Link to="/order" className="view-order-btn btn btn-secondary">
              View Order
            </Link>
            <button onClick={closeModal} className="btn btn-back">
              Back
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectedMenuItem;
